import React, { FunctionComponent, useEffect, useState } from 'react'

// @ts-ignore
import { TimestampFormatter } from '@ta-interaktiv/react-publication-info'
import FeedbackMessage, {
  FeedbackMessageProps
} from '@ta-interaktiv/react-feedback-message'
// @ts-ignore
import ShareButtons from '@ta-interaktiv/react-share-buttons'
import CopyrightNotice, {
  CopyrightNoticeProps
} from '@ta-interaktiv/react-copyright-notice'
// import { UserFeedback, UserFeedbackProps } from '../userFeedback'
import { UserFeedback, UserFeedbackProps } from '@ta-interaktiv/react-user-feedback'
// @ts-ignore
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import styles from './styles.module.scss'
import { PortfolioTeaser } from './portfolioTeaser'

declare global {
  interface Window {
    project: {
      version: string
      buildTimestamp: number
    }
  }
}

type SelectedUserFeedbackProps = Partial<UserFeedbackProps>

export interface FooterProps {
  /** The article ID of the corresponding CD / Unity article. Used to set up
   *  the share buttons and display the correct publication date. */
  articleId: string

  /**
   * A table of credits: rows containing arrays that each contain the title
   * and the credited person(s). You can add HTML code to the strings you
   * pass in here,
   * they will be
   * dangerously set into the cells. Useful if you need to add additional
   * links to sources.
   */
  credits: [string, string][]

  /** Hide the portfolio teaser, if necessary */
  hidePortfolioTeaser?: boolean

  /** Hide user feedback, if necessary. */
  hideUserFeedback?: boolean

  /** Hide user copyright, if necessary. */
  hideCopyright?: boolean
}

export const Footer: FunctionComponent<
  FooterProps &
  CopyrightNoticeProps &
  FeedbackMessageProps &
  SelectedUserFeedbackProps
> = ({
  articleId,
  call,
  credits,
  displayPrivacyPolicyLink,
  fullMediaName,
  inverted,
  locale = 'de',
  mailTo,
  mediaName,
  project,
  question,
  questions,
  starColor,
  title,
  hidePortfolioTeaser = false,
  hideUserFeedback = false,
  hideCopyright = false,
  locales,
  year
}) => {
    // Set up props for dependent components
    const copyrightNoticeProps: CopyrightNoticeProps = {
      displayPrivacyPolicyLink,
      fullMediaName,
      inverted,
      locale,
      mediaName,
      year
    }

    const portfolioTeaserProps = {
      mediaName
    }

    const feedbackMessageProps: Partial<FeedbackMessageProps> = {
      call,
      inverted,
      locale,
      mailTo,
      project,
      question
    }

    const shareButtonProps = {
      articleId,
      inverted
    }

    const userFeedbackProps = {
      articleId,
      inverted,
      locale,
      questions,
      title,
      starColor
    }


    // Set up classes for inverted style
    const segmentClass = inverted ? 'inverted basic' : 'grey'
    const tableClass = inverted ? 'inverted' : ''

    const [publicationDate, setPublicationDate] = useState<number>(
      (window.project && window.project.buildTimestamp) || 0
    )

    /**
     * The domain is used to download stuff from the API.
     *
     * In the case of dasmagazin.ch or sonntagszeitung.ch, there is either no
     *  API, or the API falsely reports that access to the article is denied.
     *  So we fall back to the domain name that's currently being used.
     */
    const domain =
      mediaName === 'sonntagszeitung' || mediaName === 'dasmagazin'
        ? 'tagesanzeiger'
        : mediaName || parseHostname().publicationName

    useEffect(() => {
      fetch(`https://m.${domain}.ch/api/articles/${articleId}`)
        .then(response => {
          if (response.ok) {
            return response.json()
          }
          throw new Error(`${response.status}: ${response.statusText}`)
        })
        .then(data => {
          setPublicationDate(data.article.first_published_at)
        })
        .catch(e => {
          console.groupCollapsed('Footer')
          console.warn('API Access failed: ', e)
          console.groupEnd()
        })
    }, [articleId, domain])
    return (
      <footer
        className={`ui vertical secondary segment standalone-footer ${segmentClass} ${
          styles.footer
          }`}
      >
        <div className='ui container'>
          <div className='ui two column stackable grid'>
            {!hideUserFeedback && (
              <div className='centered row user-feedback'>
                <div className='column'>
                  <UserFeedback {...userFeedbackProps} />
                </div>
              </div>
            )}

            {!hidePortfolioTeaser && (
              <PortfolioTeaser {...portfolioTeaserProps} />
            )}

            <div className={`row credits ${styles.creditsRow}`}>
              <div className='column'>
                <table
                  className={`ui small very basic ${tableClass} definition infographic table`}
                >
                  <tbody>
                    {credits.map((row: [string, string], rowIndex: number) => (
                      <tr key={`credits-${rowIndex}`} className='top aligned'>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={`credits-${rowIndex}-${cellIndex}`}
                            dangerouslySetInnerHTML={{ __html: cell }}
                          />
                        ))}
                      </tr>
                    ))}
                    {publicationDate > 0 && (
                      <tr>
                        <td>{locales}</td>
                        <td>
                          <TimestampFormatter
                            locale={locale}
                            timestamp={publicationDate}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className={`column ${styles.feedbackAndShares}`}>
                <FeedbackMessage {...feedbackMessageProps} />
                <div className='ui fluid share container'>
                  <ShareButtons
                    {...shareButtonProps}
                    displayType={'vertical buttons'}
                    className={'basic'}
                  />
                </div>
              </div>
            </div>
            <div className='centered row copyright'>
              <div className='column'>
                <div className='ui fluid copyright container'>
                  {!hideCopyright && (
                    <CopyrightNotice {...copyrightNoticeProps} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    )
  }
